import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ComponentCanDeactivate } from 'src/app/_helpers/guards/unsaved-changes.guard';

@Injectable({
  providedIn: 'root'
})
export class TabChangeService {    
  /**
   * @description Key: Tab index of tab in which form components (Angular components where forms are hosted) reside. 
   *              Value: Array of angular components where forms are hosted. DeactivationAwareDirective must be applied to each angular component.
   *              Note: This is applicable when separate angular components (wrapped within mat-tab) are used to host form
   * */
  private tabComponents: Map<number, Array<ComponentCanDeactivate>> = new Map();
  /**
   * @description Key: Tab index of tab in which forms reside. 
   *              Value: Array of angular formGroups. DeactivationAwareDirective must be applied to each angular component.
   *              Note: This is applicable when form is hosted within mat-tab without needing separate angular component
   * */
  private tabFormGroups: Map<number, Array<FormGroup>> = new Map();
  /**
   * @description Key: Tab index of tab in which forms reside.
   *              Value: Boolean value indicating if form should be marked pristine when user decides to navigate away without saving.
   *              Note: This is applicable when form is hosted within mat-tab without needing separate angular component. Currently 
   *                    feature to mark form pristine has not been applied to tabComponents i.e., when forms are hosted in separate angular components.
   * */
  private tabIndexToMarkPristine: Map<number, boolean> = new Map();
  
  
  currentTabIndex = 0;

  registerComponent(index: number, component: any): void {
    let tabComponentsInGivenIndex = this.tabComponents.get(index);
    
    if (!tabComponentsInGivenIndex || !(tabComponentsInGivenIndex.length > 0)) {
      tabComponentsInGivenIndex = [];
      this.tabComponents.set(index, tabComponentsInGivenIndex)
    }

    tabComponentsInGivenIndex.push(component);
  }

  registerFormGroup(index: number, formGroup: FormGroup, markFormPristine: boolean): void {
    let formGroupsInGivenIndex = this.tabFormGroups.get(index);    
    if (!formGroupsInGivenIndex || !(formGroupsInGivenIndex.length > 0)) {
      formGroupsInGivenIndex = [];
      this.tabFormGroups.set(index, formGroupsInGivenIndex)
    }
    formGroupsInGivenIndex.push(formGroup);

    this.tabIndexToMarkPristine.set(index, markFormPristine);
  }

  canDeactivateTab() {
    const tabComponentsInGivenIndex = this.tabComponents.get(this.currentTabIndex);   
    
    if(tabComponentsInGivenIndex && tabComponentsInGivenIndex.length > 0) {      
      for (let each of tabComponentsInGivenIndex) {
        if(!each.canDeactivate()) {
          return false;
        }
      }
    } else {
      const formGroups = this.tabFormGroups?.get(this.currentTabIndex);

      if (formGroups && formGroups.length > 0) {
        for (let each of formGroups) {
          if(each.dirty) {
            return false;
          }
        }
      } 
    }

    return true;
  }

  setCurrentIndex(index: number): void {
    this.currentTabIndex = index;
  }

  getCurrentComponent(): any {
    return this.tabComponents.get(this.currentTabIndex);
  }

  clear(): void {
    this.tabComponents.clear();
    this.tabFormGroups.clear();
    this.tabIndexToMarkPristine.clear();
    this.currentTabIndex = 0;
  }

  markPristineIfApplicable(index: number) {
    const markPristine = this.tabIndexToMarkPristine.get(index);
    if(markPristine) {
      let formGroupsInGivenIndex = this.tabFormGroups.get(index);
      if(formGroupsInGivenIndex?.length > 0) {
        if (formGroupsInGivenIndex && formGroupsInGivenIndex.length > 0) {
          for (let each of formGroupsInGivenIndex) {            
            each.markAsPristine();
            each.markAsUntouched();
          }
        } 
      }
    }
  }

}
